import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import React, { useEffect } from 'react';
import { titleize, deferUntilNewTitle } from '@moda/portal-stanchions';
import { tc } from '../../lib/trackingContext';
import { SEARCH_SORT_OPTIONS } from '../../constants';
import { PAGE_FILTER_FRAGMENT } from '../../components/PageFilter';
import {
  ProductListPageSearchQuery,
  ProductListPageSearchQueryVariables
} from '../../generated/types';
import { usePLPParams } from '../../hooks/usePLPParams';
import { PRODUCT_LIST_PAGE_RESULT_FRAGMENT, ProductListing } from '../../components/ProductListing';
import { NotFoundPage } from '../NotFoundPage';

export const PRODUCT_LIST_PAGE_SEARCH_QUERY: TypedDocumentNode<
  ProductListPageSearchQuery,
  ProductListPageSearchQueryVariables
> = gql`
  query ProductListPageSearchQuery(
    $after: String!
    $attributes: [String]
    $availability: [AvailabilityInput]
    $categoryPath: String
    $countryCode: String
    $designers: [String]
    $dynamicReRanking: Boolean
    $filterColors: [String]
    $materials: [String]
    $onSale: Boolean
    $priceRange: PriceRange
    $q: String!
    $markdownPercentages: [String]
    $scoring: String
    $sizes: [String]
    $versionId: Int
  ) {
    searchResult: search(
      input: {
        attributes: $attributes
        availability: $availability
        category_path: $categoryPath
        country_code: $countryCode
        designers: $designers
        dynamic_reranking: $dynamicReRanking
        filter_colors: $filterColors
        materials: $materials
        on_sale: $onSale
        price_range: $priceRange
        q: $q
        rounded_markdown_percentages: $markdownPercentages
        scoring: $scoring
        sizes: $sizes
        version_id: $versionId
      }
    ) {
      ...ProductListPageResultFragment
      ...PageFilterFragment
    }
  }
  ${PRODUCT_LIST_PAGE_RESULT_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
`;

export const ProductSearchListingPage: React.FC = () => {
  const { params, queryVariables } = usePLPParams();
  const searchQuery = params.q || '';
  const {
    data: currentData,
    previousData,
    loading,
    error
  } = useQuery(PRODUCT_LIST_PAGE_SEARCH_QUERY, {
    variables: { ...queryVariables, q: searchQuery }
  });

  const data = currentData ?? (loading ? undefined : previousData);

  const hasEmptyResults = data?.searchResult?.totalItems === 0;
  const headerText = hasEmptyResults
    ? 'Search Results'
    : searchQuery
      ? titleize(searchQuery)
      : 'Search All';
  const searchType = searchQuery ? 'standard' : 'null search';
  const description =
    'Discover & shop all the latest fashion, jewelry & home at Moda Operandi, from designers including Isabel Marant, Oscar de la Renta, Johanna Ortiz, Prada & more.';

  useEffect(() => {
    deferUntilNewTitle(() => {
      tc.track('Products Searched', {
        addToScopeOnce: { query: searchQuery, searchType },
        addToTrackingEvent: { label: searchQuery }
      });

      if (data && data.searchResult?.variants?.edges?.length === 0) {
        tc.track('Received Empty Search Result', {
          addToScopeOnce: { query: searchQuery, searchType },
          addToTrackingEvent: { label: searchQuery }
        });
      }
    });
  }, [data, searchQuery, searchType]);

  if (data && !data.searchResult) return <NotFoundPage />;

  return (
    <ProductListing
      title="Search"
      description={description}
      headerText={headerText}
      queryResult={data?.searchResult}
      error={error}
      availableSorts={SEARCH_SORT_OPTIONS}
      loading={loading}
    />
  );
};

export default ProductSearchListingPage;
